// lib/einstellungenWirkung.js — welche Felder der Seite Verwaltung ›
// Turnier-Einstellungen WIRKEN, und welche ausgeblendet sind.
//
// Entscheidung des Inhabers 23.10.2026 (Muster_Turnier_Einstellungen.html,
// Variante A): Felder, die gespeichert, aber nirgends gelesen werden, stehen
// nicht mehr als Eingabe da. Ihre Werte bleiben gespeichert (die Seite schickt
// das ganze `disciplines`-Objekt unverändert mit) und sie werden HIER, an EINER
// Stelle, wieder eingeblendet: in AUSGEBLENDET bei dem Feld `sichtbar: true`
// setzen, sobald Wertung, Zeitplan oder Kampfuhr es auswerten. Die Seite
// zeigt es dann mit Bezeichnung, Einheit und Grenzen aus dem Eintrag; im
// Hinweis „Gespeichert, aber ohne Wirkung“ verschwindet es von selbst.
//
// Gemessen am 23.10.2026 (Plan_Einstellungen_Auswerten.md, Abschnitt 1):
//   Zeitplan  frontend/src/components/dashboard/ZeitplanDashboard.jsx,
//             calculateDuration → lib/kampfRegeln.js (seit 23.10.2026, Plan
//             P1: dazu Kampfdauer, Rumble-Runden, Verlängerung als Reserve,
//             Erholungszeit, Finale/Kids, Schalter oben).
//   Kampfrichter backend/controllers/formenController.js,
//             kampfrichterAusEinstellungen → utils/kampfrichterZahl.js.
//   Wertung   wertungs_system (bracketsController, Baum ja/nein),
//             auto_dq_ab_verwarnung (services/autoDisqualifikation.js).
//
// CommonJS, damit der Backend-Test (utils/__tests__/einstellungenWirkung.test.js)
// die Datei laden kann; webpack nimmt sie genauso.

/* ═══ Feldarten ═══ */
const KR = require('./kampfRegeln');

// Dauern (einheit 'Min', typ 'dauer') nehmen seit 25.10.2026 Sekunden an
// („1:30“, „90 s“, „1,5“); gespeichert in Minuten mit Bruchteil, min/max in
// Minuten (lib/kampfRegeln.js dauerSekunden).
const FELDER = {
  durationPerPerson:  { label: 'Pro Person', einheit: 'Min', typ: 'dauer', min: 0.5, max: 15 },
  anzahlKampfrichter: { label: 'Kampfrichter', einheit: '', min: 1, max: 9 },
  breakAfter:         { label: 'Pause danach', einheit: 'Min', typ: 'dauer', min: 0, max: 10 },
  rounds:             { label: 'Runden', einheit: '', min: 1, max: 5 },
  roundDuration:      { label: 'Rundendauer', einheit: 'Min', typ: 'dauer', min: 0.5, max: 5 },
  breakBetweenRounds: { label: 'Pause zw. Runden', einheit: 'Min', typ: 'dauer', min: 0, max: 3 },
  durationPerMatch:   { label: 'Dauer je Begegnung', einheit: 'Min', typ: 'dauer', min: 0.5, max: 15 },
};

// Was gilt, wenn ein Feld fehlt: utils/kampfRegeln.js (HART), wie bisher
// ZeitplanDashboard.calculateDuration.
const ZEITPLAN_HART = KR.HART;
const KAMPFRICHTER_STANDARD = 3; // backend/utils/kampfrichterZahl.js

/* ═══ Zeilen des Zeitplans ═══
   Seit 23.10.2026 (Entscheidung F1) eine Zeile je Katalog-Disziplin
   (divlisttdaalle.Division_Type). Der Zeitplan erkennt die Disziplin am
   division_type der Anmeldung, nicht mehr am Code. Gerechnet wird in
   lib/kampfRegeln.js (Abschrift von backend/utils/kampfRegeln.js):
   person = Starter × (Dauer + Pause), runden/kampfdauer/rumble = (n−1)
   Kämpfe × (Kampf + Pause danach), Finale eigens, Erholung als Untergrenze,
   Reserve für Verlängerungen. Die Alt-Zeilen „Kickboxen“, „Freefight“,
   „Vollkontakt“ stehen nicht mehr da; „Kickboxen“ gilt nur noch als
   Rückfall für K1, Fullcontact und Continous Fighting ohne eigene Werte. */
const ZEITPLAN_ZEILEN = [
  // Kampfrichter der Formen seit 23.10.2026 (F10) unter Wertung › Formen
  { schluessel: 'Formen', name: 'Formen / Hyong / Tul', gruppe: 'Formen', art: 'person',
    felder: ['durationPerPerson', 'breakAfter'] },
  { schluessel: 'Selbstverteidigung', name: 'Selbstverteidigung', gruppe: 'Formen', art: 'person',
    felder: ['durationPerPerson', 'anzahlKampfrichter', 'breakAfter'],
    // Bei SV ist anzahl_kampfrichter der Kategorie die Zahl der Übungen (SVSchreiberPage)
    beschriftung: { anzahlKampfrichter: { label: 'Übungen', tipp: 'neue SV-Kategorien: Zahl der Übungen' } } },
  { schluessel: 'Pointfighting', name: 'Pointfighting', gruppe: 'Kampf mit Runden', art: 'runden',
    felder: ['rounds', 'roundDuration', 'breakBetweenRounds', 'breakAfter'] },
  { schluessel: 'Continous Fighting', name: 'Continous Fighting', gruppe: 'Kampf mit Runden', art: 'runden',
    felder: ['rounds', 'roundDuration', 'breakBetweenRounds', 'breakAfter'] },
  { schluessel: 'K1', name: 'K1', gruppe: 'Kampf mit Runden', art: 'runden',
    felder: ['rounds', 'roundDuration', 'breakBetweenRounds', 'breakAfter'] },
  { schluessel: 'Fullcontact', name: 'Fullcontact', gruppe: 'Kampf mit Runden', art: 'runden',
    felder: ['rounds', 'roundDuration', 'breakBetweenRounds', 'breakAfter'] },
  { schluessel: 'Rumble', name: 'Rumble', gruppe: 'Kampf mit Runden', art: 'rumble',
    felder: ['durationPerMatch', 'breakAfter'],
    beschriftung: { durationPerMatch: { label: 'Dauer bis 3 Gürtel', tipp: 'geschätzt; höchstens Runden × Rundendauer' } } },
  { schluessel: 'Kumite', name: 'Kumite', gruppe: 'Einzelkampf', art: 'kampfdauer', felder: ['breakAfter'] },
  { schluessel: 'Grappling', name: 'Grappling / BJJ', gruppe: 'Einzelkampf', art: 'kampfdauer', felder: ['breakAfter'] },
  { schluessel: 'Bruchtest', name: 'Bruchtest', gruppe: 'Sonstige', art: 'person',
    felder: ['durationPerPerson', 'breakAfter'] },
];
const KAMPF_ZEILEN = ['Pointfighting', 'Continous Fighting', 'K1', 'Fullcontact', 'Rumble', 'Kumite', 'Grappling'];

/* ═══ AUSGEBLENDET — hier freischalten ═══
   Je Eintrag: bereich, zeilen (Schlüssel, für die er gilt), feld (Schlüssel
   im JSON bzw. Spalte), Bezeichnung/Grenzen für die Eingabe, warum (steht im
   Hinweis), sichtbar. Für `art: 'unterteilung'` zeigt die Seite die
   sichtbaren Felder der Zeile noch einmal je Unterzeile (finale, kids,
   kids_finale), Werte unter disciplines.<Zeile>.<unter>.<feld>.
   `wirkt` sagt, wo der Wert seit dem Freischalten ausgewertet wird. */
const AUSGEBLENDET = [
  // ── Zeitplan, Kampfzeilen ──
  { id: 'kampf.matchDauer', bereich: 'zeitplan', zeilen: ['Kumite', 'Grappling'], feld: 'matchDauer',
    label: 'Kampfdauer', einheit: 'Min', typ: 'dauer', min: 0.5, max: 10, vorn: true,
    warum: 'Kampfdauer: der Zeitplan rechnet Runden × Rundendauer', sichtbar: true,
    wirkt: 'Zeitplan und Kampfuhr (eine Kampfzeit); leer: wie bisher Runden × Rundendauer' },
  { id: 'rumble.runden', bereich: 'zeitplan', zeilen: ['Rumble'], feld: 'rounds', label: 'Runden', einheit: '', min: 1, max: 5, vorn: true,
    warum: 'Runden: Rumble rechnet der Zeitplan mit der Dauer je Begegnung', sichtbar: true,
    wirkt: 'Kampfuhr (Zeitrahmen); Zeitplan: Obergrenze der Dauer bis 3 Gürtel' },
  { id: 'rumble.rundendauer', bereich: 'zeitplan', zeilen: ['Rumble'], feld: 'roundDuration', label: 'Rundendauer', einheit: 'Min', typ: 'dauer', min: 0.5, max: 5, vorn: true,
    warum: 'Rundendauer: Rumble rechnet der Zeitplan mit der Dauer je Begegnung', sichtbar: true, wirkt: 'Kampfuhr und Zeitplan: Zeitrahmen' },
  { id: 'rumble.rundenpause', bereich: 'zeitplan', zeilen: ['Rumble'], feld: 'breakBetweenRounds', label: 'Pause zw. Runden', einheit: 'Min', typ: 'dauer', min: 0, max: 3, vorn: true,
    warum: 'Pause zwischen Runden: Rumble rechnet der Zeitplan ohne Runden', sichtbar: true, wirkt: 'Kampfuhr und Zeitplan: Zeitrahmen' },
  { id: 'kampf.verlaengerung', bereich: 'zeitplan', zeilen: KAMPF_ZEILEN, feld: 'verlängerungsDauer',
    label: 'Verlängerung', einheit: 'Min', typ: 'dauer', min: 0, max: 5,
    // Schlüssel mit Umlaut, so steht er in den gespeicherten Daten
    warum: 'Verlängerung: weder Zeitplan noch Kampfuhr lesen sie (Uhr: utils/verlaengerung.js, je Matte)', sichtbar: true,
    wirkt: 'Zeitplan (Reserve); Kampfuhr bei Gleichstand (eine Verlängerung, danach Kampfrichterentscheid; 0 = gleich Entscheid)' },
  { id: 'kampf.goldenPoint', bereich: 'zeitplan', zeilen: KAMPF_ZEILEN, feld: 'goldenPoint', typ: 'schalter',
    label: 'Goldener Punkt',
    warum: 'Goldener Punkt: kein Ablauf liest ihn (Gleichstand entscheidet das Schreiber-Panel)', sichtbar: true,
    wirkt: 'Gleichstand: statt der Verlängerung entscheidet der erste gültige Punkt (Uhr = Verlängerung), ohne Punkt die Kampfrichter' },
  { id: 'kampf.erholungszeit', bereich: 'zeitplan', zeilen: KAMPF_ZEILEN, feld: 'erholungszeit',
    label: 'Erholungszeit', einheit: 'Min', typ: 'dauer', min: 0, max: 30,
    warum: 'Erholungszeit: der Zeitplan rechnet nur den festen Doppelstarter-Puffer', sichtbar: true,
    wirkt: 'Zeitplan (Untergrenze je Kategorie), Warnung im Schreiber-Panel beim Kampf' },
  { id: 'kampf.unterteilung', bereich: 'zeitplan', zeilen: KAMPF_ZEILEN, feld: 'finale/kids/kids_finale', art: 'unterteilung',
    unter: [['finale', 'Finale'], ['kids', 'Kids · Vorrunde'], ['kids_finale', 'Kids · Finale']],
    warum: 'Eigene Werte für Finale und Kids: der Zeitplan rechnet jeden Kampf gleich', sichtbar: true,
    wirkt: 'Finale = nur der Endkampf, Kids = Altersklasse bis 12; Zeitplan und Kampfuhr' },
  { id: 'sv.techniken', bereich: 'zeitplan', zeilen: ['Selbstverteidigung'], feld: 'anzahlTechniken',
    label: 'Anz. Techniken', einheit: '', min: 1, max: 10,
    warum: 'Anzahl Techniken: wird nirgends gelesen (die Übungszahl kommt aus „Übungen“; Regelfrage F8 zurückgestellt)', sichtbar: false },
  { id: 'formen.kampfrichter', bereich: 'zeitplan', zeilen: ['Formen'], feld: 'anzahlKampfrichter',
    label: 'Kampfrichter', einheit: '', min: 1, max: 9,
    warum: 'Kampfrichter der Formen: steht seit 23.10.2026 unter Wertung › Formen. Ein hier je Turnier gespeicherter Wert gilt nur noch, solange die Wertung des Turniers keine Zahl hat.', sichtbar: false },
  { id: 'formen.streichen', bereich: 'zeitplan', zeilen: ['Formen'], feld: 'schlechtestenStreichen', typ: 'schalter',
    label: 'Streichwertung',
    warum: 'Streichwertung: steht im Turnier unter Einstellungen › Modi', sichtbar: false },
  // ── Zeitplan, Schalter oben (disciplines._optionen) ──
  { id: 'zeitplan.trennVorrundeFinale', bereich: 'zeitplan', zeilen: ['_basis'], feld: 'trennVorrundeFinale', typ: 'schalter', optionen: true,
    label: 'Vorrunde / Finale unterscheiden',
    warum: 'Vorrunde/Finale unterscheiden: gespeichert, ausgewertet noch nicht (Plan P1)', sichtbar: true,
    tipp: 'eigene Zeiten für den Endkampf' },
  { id: 'zeitplan.trennAltersgruppen', bereich: 'zeitplan', zeilen: ['_basis'], feld: 'trennAltersgruppen', typ: 'schalter', optionen: true,
    label: 'Erwachsene / Kids trennen',
    warum: 'Erwachsene/Kids trennen: gespeichert, ausgewertet noch nicht (Plan P1)', sichtbar: true,
    tipp: 'eigene Zeiten für Altersklassen bis 12 Jahre' },
  { id: 'zeitplan.verlaengerungReserve', bereich: 'zeitplan', zeilen: ['_basis'], feld: 'verlaengerungReserve', optionen: true,
    label: 'Reserve für Verlängerungen', einheit: '%', min: 0, max: 100, standard: KR.RESERVE_STANDARD,
    warum: 'Reserve für Verlängerungen', sichtbar: true,
    tipp: `Anteil der Kämpfe, für die der Zeitplan eine Verlängerung einplant (Standard ${KR.RESERVE_STANDARD} %)` },
  // ── Standard-Wertung (wertungs_einstellungen) ──
  // Notenbereich und Kampfrichterzahl neuer Formen-Kategorien seit 23.10.2026
  // (F9, F10) aus den Wertungs-Einstellungen: backend/utils/formenVorgaben.js.
  // Nur bei Formen; Selbstverteidigung bleibt fest (F8 zurückgestellt).
  { id: 'wertung.punkte_min_farbgurt', bereich: 'wertung', feld: 'punkte_min_farbgurt', label: 'Farbgurte von', min: 0, max: 10, nurPunkte: true, nurTypen: ['Formen'],
    warum: 'Punktebereich: neue Formen-Kategorien bekommen fest 6–8 bzw. 8–10 (formenController)', sichtbar: true,
    wirkt: 'Notenbereich neuer Formen-Kategorien (Farbgurte)' },
  { id: 'wertung.punkte_max_farbgurt', bereich: 'wertung', feld: 'punkte_max_farbgurt', label: 'Farbgurte bis', min: 0, max: 10, nurPunkte: true, nurTypen: ['Formen'],
    warum: 'Punktebereich Farbgurte bis', sichtbar: true },
  { id: 'wertung.punkte_min_schwarzgurt', bereich: 'wertung', feld: 'punkte_min_schwarzgurt', label: 'Schwarzgurte von', min: 0, max: 10, nurPunkte: true, nurTypen: ['Formen'],
    warum: 'Punktebereich Schwarzgurte von', sichtbar: true },
  { id: 'wertung.punkte_max_schwarzgurt', bereich: 'wertung', feld: 'punkte_max_schwarzgurt', label: 'Schwarzgurte bis', min: 0, max: 10, nurPunkte: true, nurTypen: ['Formen'],
    warum: 'Punktebereich Schwarzgurte bis', sichtbar: true },
  { id: 'wertung.anzahl_kampfrichter', bereich: 'wertung', feld: 'anzahl_kampfrichter', label: 'Kampfrichter', min: 1, max: 9, nurPunkte: true, nurTypen: ['Formen'],
    warum: 'Kampfrichter: wirksam ist die Zahl im Zeitplan (Formen, Selbstverteidigung)', sichtbar: true,
    wirkt: 'Kampfrichterzahl neuer Formen-Kategorien (Streichwertung ab 5)' },
  { id: 'wertung.beschreibung', bereich: 'wertung', feld: 'beschreibung', typ: 'text', label: 'Beschreibung',
    warum: 'Beschreibung: wird nirgends angezeigt außer hier', sichtbar: false },
];

/* ═══ Abfragen ═══ */
const zeileVon = (schluessel) => ZEITPLAN_ZEILEN.find((z) => z.schluessel === schluessel) || null;

/** Ausgeblendete (bzw. wieder eingeblendete) Einträge einer Zeitplanzeile. */
function zusatzFuer(schluessel, sichtbar) {
  return AUSGEBLENDET.filter((a) => a.bereich === 'zeitplan' && a.zeilen.includes(schluessel) && !!a.sichtbar === sichtbar);
}

/** Eingabefelder einer Zeitplanzeile: wirksame + freigeschaltete (ohne Unterteilung).
    Freigeschaltete mit `vorn` stehen vor den Grundfeldern, die übrigen vor „Pause danach“. */
function zeitplanFelder(schluessel) {
  const z = zeileVon(schluessel);
  const basis = (z ? z.felder : []).map((f) => {
    const b = (z.beschriftung && z.beschriftung[f]) || {};
    return { feld: f, ...FELDER[f], ...b };
  });
  const frei = zusatzFuer(schluessel, true).filter((a) => a.art !== 'unterteilung')
    .map((a) => ({ feld: a.feld, label: a.label, einheit: a.einheit || '', min: a.min, max: a.max, typ: a.typ, zusatz: true, vorn: !!a.vorn }));
  const vorn = frei.filter((f) => f.vorn);
  const hinten = frei.filter((f) => !f.vorn);
  const pause = basis.filter((f) => f.feld === 'breakAfter');
  return vorn.concat(basis.filter((f) => f.feld !== 'breakAfter'), hinten, pause);
}

/** Eingabefelder der Standard-Wertung, die eingeblendet sind (ohne Wertungssystem und Auto-DQ). */
// typ: Divisionstyp der Zeile; Felder mit nurTypen gelten nur dort (bei den
// anderen stehen sie unter „ohne Wirkung“)
const passtTyp = (a, typ) => !a.nurTypen || !typ || a.nurTypen.includes(typ);
/** Wirkt ein Feld der Wertungs-Einstellungen bei diesem Divisionstyp? (auch für
    den Reiter „Wertung je Disziplin“ im Turnier, 25.10.2026) */
const wertungFeldWirkt = (feld, typ) => {
  const a = AUSGEBLENDET.find((x) => x.bereich === 'wertung' && x.feld === feld);
  return !a || (!!a.sichtbar && passtTyp(a, typ));
};
const wertungZusatzFelder = (typ) => AUSGEBLENDET.filter((a) => a.bereich === 'wertung' && a.sichtbar && passtTyp(a, typ));
const ausgeblendetWertung = (typ) => AUSGEBLENDET.filter((a) => a.bereich === 'wertung' && (!a.sichtbar || !passtTyp(a, typ)))
  .map((a) => (a.sichtbar ? { ...a, warum: `${a.label}: wirkt nur bei ${a.nurTypen.join(', ')} (neue Kategorien)` } : a));

/**
 * Mit welchem Wert rechnet der Zeitplan? lib/kampfRegeln.js: eigene Zeile →
 * Alt-Zeile → „Default“ → fester Wert. `lage` ({ finale, kids }) für
 * Unterzeilen; dann gelten die Unterzeilen auch bei ausgeschaltetem Schalter
 * (die Seite zeigt sie nur bei eingeschaltetem).
 */
function wirksamerZeitplanWert(disziplinen, schluessel, feld, lage) {
  const d = lage ? { ...(disziplinen || {}), _optionen: { ...((disziplinen || {})._optionen || {}), trennVorrundeFinale: true, trennAltersgruppen: true } } : disziplinen;
  const w = KR.feldWert(d, schluessel, feld, lage || {});
  if (feld === 'matchDauer' && (w === undefined || w === null || w === '')) return KR.regelnFuer(d, schluessel, lage || {}).rahmenMin;
  // ungültiger Text (Eingabe noch nicht fertig) bleibt Text, statt NaN zu werden
  return w === undefined ? undefined : typeof w === 'boolean' ? w : Number.isFinite(Number(w)) ? Number(w) : w;
}

/** Abschrift von backend/utils/kampfrichterZahl.js (Test vergleicht). */
function zahlAusZeile(disziplinen, divisionType) {
  if (!disziplinen || typeof disziplinen !== 'object') return null;
  const eintrag = disziplinen[divisionType] || disziplinen.Formen;
  const zahl = parseInt(eintrag && eintrag.anzahlKampfrichter, 10);
  return Number.isInteger(zahl) && zahl >= 1 && zahl <= 9 ? zahl : null;
}
function wirksameKampfrichter(turnierDisziplinen, standardDisziplinen, divisionType) {
  return zahlAusZeile(turnierDisziplinen, divisionType)
    ?? zahlAusZeile(standardDisziplinen, divisionType)
    ?? KAMPFRICHTER_STANDARD;
}

/** Minuten, die der Zeitplan für eine Kategorie mit `starter` Startern ansetzt (lib/kampfRegeln.js). */
function zeitplanMinuten(disziplinen, schluessel, starter = 8, kids = false, platzDrei = false) {
  return KR.kategorieMinuten(disziplinen, schluessel, { starter, kids, platzDrei });
}

module.exports = {
  FELDER, ZEITPLAN_HART, KAMPFRICHTER_STANDARD, ZEITPLAN_ZEILEN, KAMPF_ZEILEN, AUSGEBLENDET,
  zeileVon, zusatzFuer, zeitplanFelder, wertungZusatzFelder, ausgeblendetWertung, wertungFeldWirkt,
  wirksamerZeitplanWert, wirksameKampfrichter, zeitplanMinuten,
};
